import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { createScannedDocument } from '../../../../../../services/api/scannedDocuments';
import { useIntl } from 'react-intl';
import { concreteTypes } from '../../../../../../constants/general';
import ScannedDocumentsLayout from '../../Commons/ScannedDocumentsLayout';
import { MainContext } from '../../../../../../context/MainContext';
import LayoutHeader from '../Commons/LayoutHeader';
import SuccessPage from '../../Commons/ScannedDocumentsLayout/components/SuccessPage';
import {
  getProjectTemplateById,
  updateProjectTemplate,
} from '../../../../../../services/api/projectTemplates';
import { useDispatch } from 'react-redux';
import { deletePreProcessedDocument } from '../../../../../../services/redux/preProcessedDocuments/actions';
import DeliveryFormFields from '../Commons/DeliveryFormFields';
import {
  deliveryPCFieldGroups,
  deliveryRCFieldGroups,
} from '../Commons/deliveryFieldsGroups';
import { checkDiffTemplate } from '../../Commons/ScannedDocumentsLayout/utils.index';

const UpdateDeliveryScannedDoc = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { project } = useOutletContext();
  const { setLoading, setError } = useContext(MainContext);
  const { sdId, model, templateId, preprocessId } = useParams();
  const [initialData, setInitialData] = useState(null);
  const [success, setSuccess] = useState(false);
  const [diffTemplate, setDiffTemplate] = useState(false);
  const [template, setLoadedTemplate] = useState(null);
  const noTemplateMsg = intl.formatMessage({ id: 'noTemplate' });
  const noSupplierMsg = intl.formatMessage({ id: 'noSuppliers' });
  const navigate = useNavigate();
  const deliveryFieldGroups = useMemo(() => {
    return model === 'delivery' ? deliveryRCFieldGroups : deliveryPCFieldGroups;
  }, [model]);
  const validableFields = useMemo(
    () =>
      deliveryFieldGroups.mandatory.filter((field) => field !== 'limit_time'),
    [deliveryFieldGroups],
  );

  const createDeliveryScannedDocument = async (
    regions,
    formState,
    fileName,
  ) => {
    try {
      const scannedDocument = await createScannedDocument(
        {
          fileName,
          template: templateId,
          pid: project.id_project,
          camFileName: formState.camFileName,
          delivery_note_data: {
            ...formState,
          },
          regions: regions,
        },
        model,
      );

      dispatch(deletePreProcessedDocument(preprocessId));

      if (scannedDocument.data) {
        setSuccess(true);
        if (checkDiffTemplate(regions, template.regions)) {
          setDiffTemplate(regions);
        }
      }
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const loadTemplate = async () => {
      try {
        setLoading(true);
        const loadedTemplate = (
          await getProjectTemplateById({ id: templateId })
        ).data;
        setLoadedTemplate(loadedTemplate);
        setInitialData({
          form: {
            templateName: loadedTemplate.name,
            supplier_id: loadedTemplate.supplier,
          },
          regions: loadedTemplate.regions,
        });
      } catch (error) {
        setError(error);
        setLoading(false);
      }
    };
    loadTemplate();
  }, [
    setLoading,
    setInitialData,
    sdId,
    noTemplateMsg,
    noSupplierMsg,
    model,
    setError,
    deliveryFieldGroups,
    templateId,
  ]);

  useLayoutEffect(() => {
    if (!concreteTypes.includes(model)) {
      navigate('/');
    }
  }, [model, navigate]);

  if (success) {
    return (
      <SuccessPage
        title={intl.formatMessage({
          id: 'pages.ocr.scannedDocuments.delivery.create.success.title',
        })}
        subtitle={intl.formatMessage({
          id: 'pages.ocr.scannedDocuments.delivery.create.success.subtitle',
        })}
        checkDiffTemplate={Boolean(diffTemplate)}
        onBackToOcr={() =>
          navigate(`/app/project/${project.id_project}/quality/ocr`)
        }
        onSaveTemplate={async () => {
          const newTemplate = { ...template, regions: diffTemplate };
          setLoading(true);
          await updateProjectTemplate(newTemplate);
          setLoading(false);
          navigate(`/app/project/${project.id_project}/quality/ocr`, {
            replace: true,
          });
        }}
      />
    );
  }
  return (
    <ScannedDocumentsLayout
      model={model}
      topFormFields={<DeliveryFormFields model={model} />}
      initialData={initialData}
      fieldGroups={deliveryFieldGroups}
      validableFields={validableFields}
      onFinish={createDeliveryScannedDocument}
      onBack={() =>
        navigate(
          `/app/project/${project.id_project}/quality/ocr/preProcessedDocuments`,
        )
      }
      header={
        <LayoutHeader
          template={initialData?.form?.templateName}
          supplier={initialData?.form?.supplier_id}
        />
      }
    />
  );
};

export default UpdateDeliveryScannedDoc;
